import { useAppDispatch } from "@/reducer";
import { setSelectedTool } from "@/reducer/UIState";
import { t } from "@lingui/core/macro";
import {
  type HasLayerIdxNarrowing,
  type PartialWeatherParameter,
  parameterToString,
} from "@mm/weather-parameter-utils";
import { cloneDeep } from "lodash";

import { useUser } from "@/api/hooks/user";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components";
import { EditableTextbox } from "@/overlay/components/CommonParts/EditableTextbox";
import { Button, ButtonBar } from "@/overlay/components/CommonParts/buttons";
import { TableExportMenu } from "@/overlay/components/LayerStackWindow/TableExportMenu";
import { WeatherTableActionPanel } from "@/overlay/components/LayerStackWindow/WeatherTableActionPanel";
import { GroupsStack } from "@/overlay/components/LayerStackWindow/WeatherTableSettings/ColumnsStack";
import {
  triggerWeatherTableExport,
  triggerWeatherTablePdfExport,
  updateWeatherTableState,
} from "@/reducer/WeatherTablesState";
import { getFeatureValue } from "@/types/featureSet";
import { hasCalibratedAvailable } from "@/utility/calibratedAvailabilityMap";
import type { WeatherTable, WeatherTableColumn, WeatherTableGroup } from "@/weatherTableView/types";
import { useCallback } from "react";

interface TableSettingProps {
  weatherTable: WeatherTable;
  clearWeatherTable: () => void;
}

export function WeatherTableSetting({ weatherTable, clearWeatherTable }: TableSettingProps) {
  const desktop = useDesktop();
  const dispatch = useAppDispatch();
  const { user, features } = useUser();
  const hasExport = !!getFeatureValue(features, "ui.csv_export");
  const exportAllowed = user?.is_developer || user?.is_superuser || hasExport;

  const handleRemoveColumn = useCallback(
    (groupIdx: number, column: WeatherTableColumn) => {
      desktop.dispatch({
        type: "confirmationWindow",
        windowId: instanceId(WindowKind.ConfirmationWindow),
        customProps: {
          message: t`Do you want to delete parameter "${column.parameterUnit}"?`,
          callback: (confirmed: boolean) => {
            if (!confirmed) {
              return;
            }

            // Messy code to remove the column
            // TODO Refactor this to make it more readable or do removal on backend
            const updatedTable: Partial<WeatherTable> = {
              ...weatherTable,
              groups: weatherTable.groups.map((group, index) => {
                if (index === groupIdx) {
                  return {
                    ...group,
                    columns: group.columns.filter((col) => col.index !== column.index),
                  };
                }

                return group;
              }),
            };

            dispatch(
              updateWeatherTableState({
                id: weatherTable.id,
                weatherTable: updatedTable,
              }),
            );
          },
        },
      });
    },
    [desktop, weatherTable, dispatch],
  );

  function addNewParameterToTable(
    table: WeatherTable,
    param: HasLayerIdxNarrowing<PartialWeatherParameter>,
    model: string | null,
  ) {
    // Add a new parameter
    const paramStrFormatted = parameterToString(param);

    // Create a new copy of the groups array
    const updatedGroups: WeatherTableGroup[] = cloneDeep([...table.groups]);
    const groupDict = updatedGroups[0];
    const updatedColumns: WeatherTableColumn[] = [...groupDict.columns];
    const len = updatedColumns.length;
    updatedColumns.push({
      title: paramStrFormatted.formatted, //TODO get a nice title from parameter
      parameterUnit: paramStrFormatted.formatted,
      model: model || "mix",
      show: true,
      index: len,
      calibrated: hasCalibratedAvailable(paramStrFormatted.formatted),
      ensSelect: "",
      color: "#000000",
      thresholds: [],
      advancedThresholds: [],
      apiUrlSuffix: undefined,
    });

    // Update the columns array in the group dictionary
    groupDict.columns = updatedColumns;
    const tableUpdate: Partial<WeatherTable> = { id: table.id, tabId: table.tabId, groups: updatedGroups }; //TODO when implementing groups, adjust here and above

    dispatch(
      updateWeatherTableState({
        id: weatherTable.id,
        weatherTable: tableUpdate,
      }),
    );
  }

  function openAddParameterWindow(weatherTable: WeatherTable) {
    dispatch(setSelectedTool(weatherTable));
    desktop.dispatch({
      type: "setWindowCustomProps",
      windowId: instanceId(WindowKind.AddParameter),
      windowKind: WindowKind.AddParameter,
      customProps: {
        source: "add-table-param",
        onSubmitParam: (param, _parameterType, model) => {
          addNewParameterToTable(weatherTable, param, model);
        },
      },
    });
    desktop.dispatch({ type: "setWindowVisibility", windowId: instanceId(WindowKind.AddParameter), isVisible: true });
  }

  const handleCSVExport = () => {
    dispatch(
      triggerWeatherTableExport({
        id: weatherTable.id,
      }),
    );
  };

  const handlePDFExport = () => {
    dispatch(
      triggerWeatherTablePdfExport({
        id: weatherTable.id,
      }),
    );
  };

  return (
    <>
      <div className="title-row">
        <EditableTextbox
          className={"editable-map-title"}
          value={weatherTable.title ?? ""}
          placeholder={weatherTable.title === "" ? t`Untitled Table` : undefined}
          blurOnEnterKey
          onSave={(newTitle) => {
            dispatch(
              updateWeatherTableState({
                id: weatherTable.id,
                weatherTable: { ...weatherTable, title: newTitle },
              }),
            );
          }}
        />
        <WeatherTableActionPanel table={weatherTable} clearWeatherTable={clearWeatherTable} />
      </div>

      <GroupsStack weatherTable={weatherTable} onRemoveColumn={handleRemoveColumn} />

      <ButtonBar align="right" className="layer-stack-actions">
        {exportAllowed && <TableExportMenu onExportCsv={handleCSVExport} onExportPdf={handlePDFExport} />}
        <Button icon="add" onClick={() => openAddParameterWindow(weatherTable)}>{t`Add Column`}</Button>
      </ButtonBar>
    </>
  );
}
